<script setup lang="ts">
import { computed } from 'vue';
import type { Component } from 'vue';
import { RouterLink } from 'vue-router';
import { HelpCircle } from 'lucide-vue-next';

type NavLink = {
  to: string;
  label: string;
  icon?: Component;
};

type UiNavLink = Omit<NavLink, 'icon'> & {
  icon: Component;
};

type NavGroup = {
  key: string;
  title?: string;
  paths: string[];
};

const navGroups: NavGroup[] = [
  {
    key: 'home',
    paths: ['/dashboard']
  },
  {
    key: 'sales',
    title: 'Продажи',
    paths: ['/sales-orders']
  },
  {
    key: 'planning-production',
    title: 'Планирование и производство',
    paths: [
      '/planning',
      '/production/route-launches',
      '/assignments',
      '/manufacturing',
      '/workplaces',
      '/workplaces/injection-molding-tooling'
    ]
  },
  {
    key: 'builders',
    title: 'Конструкторы',
    paths: ['/workflows-next']
  },
  {
    key: 'inventory-procurement',
    title: 'Склад и снабжение',
    paths: ['/inventory', '/procurement']
  },
  {
    key: 'directories',
    title: 'Справочники',
    paths: ['/counterparties']
  },
  {
    key: 'administration',
    title: 'Администрирование',
    paths: ['/users']
  }
];

const props = defineProps<{
  navLinks: NavLink[];
  themeClasses: {
    surface: string;
    muted: string;
  };
  isNavLinkActive: (to: string) => boolean;
}>();

const uiNavLinks = computed<UiNavLink[]>(() =>
  props.navLinks.map((item) => ({
    ...item,
    icon: item.icon ?? HelpCircle
  }))
);

const groupedNavLinks = computed(() => {
  const groupedLinks = new Set<UiNavLink>();
  const groups = navGroups
    .map((group) => {
      const paths = new Set(group.paths);
      const items = uiNavLinks.value.filter((item) => paths.has(item.to));
      items.forEach((item) => groupedLinks.add(item));

      return {
        key: group.key,
        title: group.title,
        items
      };
    })
    .filter((group) => group.items.length > 0);

  const ungroupedLinks = uiNavLinks.value.filter((item) => !groupedLinks.has(item));
  if (ungroupedLinks.length > 0) {
    groups.push({
      key: 'other',
      title: undefined,
      items: ungroupedLinks
    });
  }

  return groups;
});

const groupedNavItemCount = computed(() =>
  groupedNavLinks.value.reduce((total, group) => total + group.items.length, 0)
);
</script>

<template>
  <div class="main-navigation-stack">
    <nav
      data-help-id="app-main-navigation"
      class="main-navigation"
      aria-label="Основная навигация"
    >
      <header class="main-navigation__header">
        <span>Навигация</span>
        <span>{{ groupedNavItemCount }}</span>
      </header>

      <div class="main-navigation__groups">
        <section
          v-for="group in groupedNavLinks"
          :key="group.key"
          class="main-navigation__group"
        >
          <h2
            v-if="group.title"
            class="main-navigation__group-title"
          >
            {{ group.title }}
          </h2>

          <RouterLink
            v-for="item in group.items"
            :key="item.to"
            class="main-navigation__link"
            :class="{ 'main-navigation__link--active': isNavLinkActive(item.to) }"
            :to="item.to"
            :aria-current="isNavLinkActive(item.to) ? 'page' : undefined"
          >
            <span
              class="main-navigation__icon"
              aria-hidden="true"
            >
              <component
                :is="item.icon"
                :size="22"
                :stroke-width="1.85"
              />
            </span>
            <span class="main-navigation__label">
              {{ item.label }}
            </span>
          </RouterLink>
        </section>
      </div>
    </nav>
  </div>
</template>

<style scoped>
.main-navigation-stack {
  box-sizing: border-box;
  position: sticky;
  top: 6rem;
  display: flex;
  width: 16rem;
  height: calc(100vh - 7rem);
  max-width: 100%;
  max-height: calc(100vh - 7rem);
  flex-direction: column;
  align-self: start;
  overflow: hidden;
  padding: 0.125rem;
}

.main-navigation-stack *,
.main-navigation-stack *::before,
.main-navigation-stack *::after {
  box-sizing: border-box;
}

.main-navigation {
  display: flex;
  width: 16rem;
  min-height: 0;
  max-width: 100%;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 1.5rem;
  border: 1px solid color-mix(in srgb, var(--ui-border) 82%, transparent);
  border-radius: 1.25rem;
  background:
    radial-gradient(circle at 18% 0%, color-mix(in srgb, var(--ui-accent) 10%, transparent) 0, transparent 32%),
    linear-gradient(180deg, color-mix(in srgb, var(--ui-surface) 94%, var(--ui-surface-muted) 6%) 0%, var(--ui-surface) 100%);
  box-shadow: var(--ui-shadow-panel);
  overflow: hidden;
  padding: 1.75rem 1rem 1.5rem;
}

.main-navigation__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--ui-foreground-quiet);
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  line-height: 0.875rem;
  padding: 0 0.5rem;
  text-transform: uppercase;
}

.main-navigation__header span:last-child {
  color: var(--ui-accent);
  font-size: 0.8125rem;
  letter-spacing: 0;
  text-transform: none;
}

.main-navigation__groups {
  display: grid;
  align-content: start;
  gap: 1.5rem;
  min-height: 0;
  min-width: 0;
  overflow-y: auto;
  padding: 0.125rem 0.25rem 0.125rem 0;
  scrollbar-gutter: stable;
}

.main-navigation__groups::-webkit-scrollbar {
  width: 0.5rem;
}

.main-navigation__groups::-webkit-scrollbar-track {
  background: transparent;
}

.main-navigation__groups::-webkit-scrollbar-thumb {
  border: 0.125rem solid transparent;
  border-radius: var(--ui-radius-full);
  background-clip: padding-box;
  background-color: color-mix(in srgb, var(--ui-foreground-quiet) 36%, transparent);
}

.main-navigation__groups::-webkit-scrollbar-thumb:hover {
  background-color: color-mix(in srgb, var(--ui-accent) 45%, transparent);
}

.main-navigation__group {
  display: grid;
  gap: 0.75rem;
  min-width: 0;
}

.main-navigation__group-title {
  margin: 0;
  max-width: 100%;
  overflow: hidden;
  color: var(--ui-foreground-quiet);
  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  line-height: 0.875rem;
  padding: 0 0.25rem;
  text-transform: uppercase;
  white-space: nowrap;
}

.main-navigation__link {
  position: relative;
  display: grid;
  width: 100%;
  min-width: 0;
  min-height: 3.5rem;
  grid-template-columns: 2rem minmax(0, 1fr);
  align-items: center;
  gap: 0.9rem;
  border-radius: 0.55rem;
  color: var(--ui-foreground-muted);
  font-size: 0.95rem;
  font-weight: 750;
  line-height: 1.2;
  padding: 0.85rem 1rem;
  text-decoration: none;
  outline: none;
  transition:
    background-color 0.16s ease,
    box-shadow 0.16s ease,
    color 0.16s ease,
    transform 0.16s ease;
}

.main-navigation__link::before {
  position: absolute;
  top: 0.75rem;
  bottom: 0.75rem;
  left: 0.5rem;
  width: 0.1875rem;
  border-radius: var(--ui-radius-full);
  background: var(--ui-accent);
  content: '';
  opacity: 0;
  transform: scaleY(0.65);
  transition:
    opacity 0.16s ease,
    transform 0.16s ease;
}

.main-navigation__link:hover {
  background: color-mix(in srgb, var(--ui-accent) 10%, var(--ui-surface));
  color: var(--ui-accent);
  transform: translateY(-1px);
}

.main-navigation__link:focus-visible {
  box-shadow: var(--ui-focus-ring);
}

.main-navigation__link--active {
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--ui-accent) 16%, var(--ui-surface)) 0%,
    color-mix(in srgb, var(--ui-accent) 10%, var(--ui-surface)) 100%
  );
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--ui-accent) 16%, transparent);
  color: var(--ui-accent);
  font-weight: 850;
}

.main-navigation__link--active::before {
  opacity: 1;
  transform: scaleY(1);
}

.main-navigation__icon {
  display: inline-flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.65rem;
  background: color-mix(in srgb, var(--ui-foreground) 4%, transparent);
  color: currentColor;
  transition:
    background-color 0.16s ease,
    color 0.16s ease;
}

.main-navigation__link:hover .main-navigation__icon,
.main-navigation__link--active .main-navigation__icon {
  background: color-mix(in srgb, var(--ui-accent) 12%, var(--ui-surface));
}

.main-navigation__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:global([data-theme='dark']) .main-navigation {
  background:
    radial-gradient(circle at 18% 0%, color-mix(in srgb, var(--ui-accent) 16%, transparent) 0, transparent 34%),
    linear-gradient(180deg, var(--ui-surface-muted) 0%, var(--ui-surface) 100%);
}

@media (max-width: 1023px) {
  .main-navigation-stack {
    position: static;
    width: 100%;
    height: auto;
    max-height: none;
    overflow: visible;
  }
}

@supports (height: 100dvh) {
  @media (min-width: 1024px) {
    .main-navigation-stack {
      height: calc(100dvh - 7rem);
      max-height: calc(100dvh - 7rem);
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .main-navigation__link,
  .main-navigation__link::before,
  .main-navigation__icon {
    transition: none;
  }

  .main-navigation__link:hover {
    transform: none;
  }
}
</style>
